import {
  useMemo,
  type ReactElement,
  type ReactNode,
  Children,
  isValidElement,
} from 'react';

import { isDev } from '../../../core/utils/is-node-environment.js';
import {
  isEmptyStateActions,
  type EmptyStateActionsProps,
} from '../slot-components/Actions.js';
import {
  isEmptyStateDetails,
  type EmptyStateDetailsProps,
} from '../slot-components/Details.js';
import {
  isEmptyStateFooter,
  type EmptyStateFooterProps,
} from '../slot-components/Footer.js';
import {
  isEmptyStateTitle,
  type EmptyStateTitleProps,
} from '../slot-components/Title.js';
import {
  isEmptyStateVisual,
  type EmptyStateVisualProps,
} from '../slot-components/Visual.js';
import {
  isEmptyStateVisualPreset,
  EmptyStateVisualPresetProps,
} from '../slot-components/VisualPreset.js';

/** @internal */
type EmptyStateSlots = {
  Actions: ReactElement<EmptyStateActionsProps> | null;
  Details: ReactElement<EmptyStateDetailsProps> | null;
  Footer: ReactElement<EmptyStateFooterProps> | null;
  Title: ReactElement<EmptyStateTitleProps> | null;
  Visual: ReactElement<EmptyStateVisualProps> | null;
  VisualPreset: ReactElement<EmptyStateVisualPresetProps> | null;
};

/**
 * Helper hook that will extract the Slots used in the empty state component and collect the data / components.
 *
 * @internal
 */
export function useEmptyStateSlots(children: ReactNode): EmptyStateSlots {
  return useMemo(() => {
    const slots: EmptyStateSlots = {
      Actions: null,
      Details: null,
      Footer: null,
      Title: null,
      Visual: null,
      VisualPreset: null,
    };

    Children.forEach(children, (child) => {
      if (isEmptyStateActions(child)) {
        slots.Actions = child;
      } else if (isEmptyStateDetails(child)) {
        slots.Details = child;
      } else if (isEmptyStateFooter(child)) {
        slots.Footer = child;
      } else if (isEmptyStateTitle(child)) {
        slots.Title = child;
      } else if (isEmptyStateVisual(child)) {
        slots.Visual = child;
      } else if (isEmptyStateVisualPreset(child)) {
        slots.VisualPreset = child;
      } else if (isValidElement(child) && isDev()) {
        // eslint-disable-next-line no-console
        console.warn(
          'The used child is not supported by EmptyState. Only Actions, Content, Details, Title, Visual, and VisualPreset are valid children of EmptyState.',
        );
      }
    });

    return slots;
  }, [children]);
}
